import React from 'react';

import { Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, InputLabel, Typography } from '@material-ui/core';
import { FormikProps, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import * as yup from 'yup';

import ButtonsGrid from 'components/_dialogs/_components/ButtonsGrid';
import DialogGrid from 'components/_dialogs/_components/DialogGrid';
import ColoredButton from 'components/ColoredButton';
import FormDateInput from 'components/FormDateInput';
import FormSelect from 'components/FormSelect/FormSelect';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import dsarApi from 'config/api/dsar/dsar';
import type { DsarCase, DsarCaseCreatePayload, DsarCaseUpdatePayload, DsarDomainValueSummary } from 'config/api/dsar/dsar.types';
import safeT from 'helpers/safeT/safeT';
import dsar_messages from 'messages/dsar_messages';
import general_messages from 'messages/general_messages';
import validation_messages from 'messages/validation_messages';

import useStyles from '../DsarCasesPage.styles';

import { fromPickerDate, getDsarDateLimits, isIsoDate, toPickerDate } from './dsarDateValidation';

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved: (dsarCase: DsarCase) => void;
  caseStatusValues: DsarDomainValueSummary[];
  domainValuesLoading: boolean;
  identityStatusValues: DsarDomainValueSummary[];
  requestTypeValues: DsarDomainValueSummary[];
  existingCase?: DsarCase | null;
};

type FormValues = {
  requesterName: string;
  requesterEmail: string;
  requesterPhone: string;
  requesterCustomerNumber: string;
  requesterSsn: string;
  requesterAddressLine1: string;
  requesterAddressLine2: string;
  requesterPostalCode: string;
  requesterCity: string;
  requesterCountry: string;
  identityVerificationStatusId: number | '';
  identityVerificationNotes: string;
  statusId: number | '';
  receivedAt: string;
  dueDate: string;
  requestTypeIds: number[];
  adminInstructionComment: string;
  instructionLink: string;
  portabilityInstruction: string;
  portabilityOutputLink: string;
};

const FORM_ID = 'DsarCaseCreateForm';
const PORTABILITY_REQUEST_TYPE_CODES = ['access', 'portability'];

const toOptions = (values?: DsarDomainValueSummary[]) =>
  (values || []).map(value => ({ key: value.id, label: [value.nameTranslationKey || '', value.name] }));

const cleanOptional = (value: string) => value.trim() || undefined;

const toDateInputValue = (value?: string | null) => {
  if (!value) return '';
  return value.split('T')[0];
};

const buildInitialValues = (
  existingCase: DsarCase | null | undefined,
  caseStatusValues: DsarDomainValueSummary[],
  identityStatusValues: DsarDomainValueSummary[],
): FormValues => {
  if (!existingCase) {
    return {
      requesterName: '',
      requesterEmail: '',
      requesterPhone: '',
      requesterCustomerNumber: '',
      requesterSsn: '',
      requesterAddressLine1: '',
      requesterAddressLine2: '',
      requesterPostalCode: '',
      requesterCity: '',
      requesterCountry: '',
      identityVerificationStatusId: identityStatusValues[0]?.id || '',
      identityVerificationNotes: '',
      statusId: caseStatusValues[0]?.id || '',
      receivedAt: '',
      dueDate: '',
      requestTypeIds: [],
      adminInstructionComment: '',
      instructionLink: '',
      portabilityInstruction: '',
      portabilityOutputLink: '',
    };
  }

  return {
    requesterName: existingCase.requesterName || '',
    requesterEmail: existingCase.requesterEmail || '',
    requesterPhone: existingCase.requesterPhone || '',
    requesterCustomerNumber: existingCase.requesterCustomerNumber || '',
    requesterSsn: existingCase.requesterSsn || '',
    requesterAddressLine1: existingCase.requesterAddressLine1 || '',
    requesterAddressLine2: existingCase.requesterAddressLine2 || '',
    requesterPostalCode: existingCase.requesterPostalCode || '',
    requesterCity: existingCase.requesterCity || '',
    requesterCountry: existingCase.requesterCountry || '',
    identityVerificationStatusId: existingCase.identityVerificationStatus?.id || identityStatusValues[0]?.id || '',
    identityVerificationNotes: existingCase.identityVerificationNotes || '',
    statusId: existingCase.status?.id || caseStatusValues[0]?.id || '',
    receivedAt: toDateInputValue(existingCase.receivedAt),
    dueDate: toDateInputValue(existingCase.dueDate),
    requestTypeIds: existingCase.requestTypes.map(({ requestType }) => requestType.id),
    adminInstructionComment: existingCase.adminInstructionComment || '',
    instructionLink: existingCase.instructionLink || '',
    portabilityInstruction: existingCase.portabilityInstruction || '',
    portabilityOutputLink: existingCase.portabilityOutputLink || '',
  };
};

const DsarCaseCreateDialog: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  caseStatusValues,
  domainValuesLoading,
  identityStatusValues,
  requestTypeValues,
  existingCase,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const isEditMode = Boolean(existingCase);
  const isDistributed = Boolean(existingCase?.distributedAt);
  const initialValues = React.useMemo(
    () => buildInitialValues(existingCase, caseStatusValues, identityStatusValues),
    [caseStatusValues, existingCase, identityStatusValues],
  );
  const verifiedIdentityStatusIds = React.useMemo(
    () => identityStatusValues.filter(value => value.code === 'verified').map(value => value.id),
    [identityStatusValues],
  );
  const validationSchema = React.useMemo(
    () =>
      yup.object({
        requesterName: yup.string().required(t(validation_messages.required)),
        requesterEmail: yup.string().email(t(validation_messages.email_valid)),
        identityVerificationStatusId: yup.number().required(t(validation_messages.required)),
        identityVerificationNotes: yup
          .string()
          .test('identity-verification-notes-required-when-verified', t(validation_messages.required), (value, context) => {
            const { identityVerificationStatusId } = context.parent as FormValues;
            if (!verifiedIdentityStatusIds.includes(Number(identityVerificationStatusId))) return true;
            return Boolean(value?.trim());
          }),
        statusId: yup.number().required(t(validation_messages.required)),
        receivedAt: yup
          .string()
          .required(t(validation_messages.required))
          .test('valid-received-date', t(validation_messages.invalid_date), value => !value || isIsoDate(value))
          .test(
            'received-not-future',
            t(validation_messages.date_cannot_be_in_future),
            value => isEditMode || !value || !isIsoDate(value) || value <= getDsarDateLimits().today,
          ),
        dueDate: yup
          .string()
          .required(t(validation_messages.required))
          .test('valid-due-date', t(validation_messages.invalid_date), value => !value || isIsoDate(value))
          .test(
            'due-not-past',
            t(validation_messages.date_cannot_be_in_past),
            value => isEditMode || !value || !isIsoDate(value) || value >= getDsarDateLimits().today,
          )
          .test(
            'due-within-90-days',
            t(validation_messages.dsar_due_date_maximum),
            value => isEditMode || !value || !isIsoDate(value) || value <= getDsarDateLimits().maximumDueDate,
          )
          .test('due-date-after-received-at', t(validation_messages.date_to_cannot_be_before_date_from), (value, context) => {
            const { receivedAt } = context.parent as FormValues;
            if (!value || !receivedAt || !isIsoDate(value) || !isIsoDate(receivedAt)) return true;
            return value >= receivedAt;
          }),
        requestTypeIds: yup.array().of(yup.number()).min(1, t(validation_messages.required)),
      }),
    [isEditMode, t, verifiedIdentityStatusIds],
  );

  const formikRef = React.useRef<FormikProps<FormValues> | null>(null);
  const mutation = useMutation(
    (payload: DsarCaseCreatePayload | DsarCaseUpdatePayload) =>
      isEditMode && existingCase
        ? dsarApi.updateCase(existingCase.id, payload as DsarCaseUpdatePayload)
        : dsarApi.createCase(payload as DsarCaseCreatePayload),
    {
      onSuccess: response => {
        enqueueSnackbar(t(general_messages.data_saved), { variant: 'success' });
        onSaved(response.data);
        onClose();
      },
      onError: (error: any) => {
        const serverDateMessages: Record<string, string[]> = {
          'Received date cannot be in the future.': validation_messages.date_cannot_be_in_future,
          'Due date cannot be in the past.': validation_messages.date_cannot_be_in_past,
          'Due date cannot be more than 90 days from today.': validation_messages.dsar_due_date_maximum,
          'Due date cannot be before received date.': validation_messages.date_to_cannot_be_before_date_from,
        };
        let hasDateError = false;
        Object.entries({ received_at: 'receivedAt', due_date: 'dueDate' }).forEach(([apiField, formField]) => {
          const errors = error?.response?.data?.[apiField];
          const message = Array.isArray(errors) ? errors[0] : errors;
          if (message && serverDateMessages[message]) {
            formikRef.current?.setFieldError(formField, t(serverDateMessages[message]));
            formikRef.current?.setFieldTouched(formField, true, false);
            hasDateError = true;
          }
        });
        if (hasDateError) return;
        enqueueSnackbar(t(general_messages.api_error), { variant: 'error' });
      },
    },
  );

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    onSubmit: values => {
      if (!values.statusId || !values.identityVerificationStatusId) return;

      const payload: DsarCaseCreatePayload = {
        requester_name: values.requesterName.trim(),
        requester_email: cleanOptional(values.requesterEmail),
        requester_phone: cleanOptional(values.requesterPhone),
        requester_customernumber: cleanOptional(values.requesterCustomerNumber),
        requester_ssn: cleanOptional(values.requesterSsn),
        requester_address_line1: cleanOptional(values.requesterAddressLine1),
        requester_address_line2: cleanOptional(values.requesterAddressLine2),
        requester_postalcode: cleanOptional(values.requesterPostalCode),
        requester_city: cleanOptional(values.requesterCity),
        requester_country: cleanOptional(values.requesterCountry),
        identity_verification_status_id: values.identityVerificationStatusId,
        identity_verification_notes: cleanOptional(values.identityVerificationNotes),
        status_id: values.statusId,
        received_at: values.receivedAt,
        due_date: values.dueDate,
        admin_instruction_comment: cleanOptional(values.adminInstructionComment),
        instruction_link: cleanOptional(values.instructionLink),
        portability_instruction: cleanOptional(values.portabilityInstruction),
        portability_output_link: cleanOptional(values.portabilityOutputLink),
        request_type_ids: values.requestTypeIds,
      };

      mutation.mutate(payload);
    },
    enableReinitialize: true,
  });
  formikRef.current = formik;

  const handleClose = React.useCallback(() => {
    formik.resetForm();
    onClose();
  }, [formik, onClose]);

  const dateLimits = getDsarDateLimits();
  const dateFormik = {
    ...formik,
    values: { ...formik.values, receivedAt: toPickerDate(formik.values.receivedAt), dueDate: toPickerDate(formik.values.dueDate) },
    setFieldValue: (field: string, value: Date | null) => formik.setFieldValue(field, fromPickerDate(value)),
  };

  const handleRequestTypeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const requestTypeId = Number(event.target.name);
      const nextValues = event.target.checked
        ? [...formik.values.requestTypeIds, requestTypeId]
        : formik.values.requestTypeIds.filter(id => id !== requestTypeId);
      formik.setFieldTouched('requestTypeIds', true, false);
      formik.setFieldValue('requestTypeIds', nextValues);
    },
    [formik],
  );

  const showPortabilityFields = requestTypeValues.some(
    requestType => formik.values.requestTypeIds.includes(requestType.id) && PORTABILITY_REQUEST_TYPE_CODES.includes(requestType.code),
  );

  const requestTypesLocked = isDistributed;
  const identityLocked = isDistributed;
  const title = isEditMode ? t(dsar_messages.cases.edit_dialog.title) : t(dsar_messages.cases.create_dialog.title);
  const intro = isEditMode ? t(dsar_messages.cases.edit_dialog.intro) : t(dsar_messages.cases.create_dialog.intro);

  const dialogActions = (
    <ButtonsGrid>
      <ColoredButton
        className=''
        customColor='secondary'
        disabled={mutation.isLoading}
        icon={false}
        onClick={handleClose}
        showLoader={false}
        textLike={false}
        variant='outlined'
      >
        {t(general_messages.cancel)}
      </ColoredButton>
      <ColoredButton
        className=''
        customColor='secondary'
        disabled={mutation.isLoading || domainValuesLoading}
        form={FORM_ID}
        icon={false}
        showLoader={mutation.isLoading}
        textLike={false}
        type='submit'
        variant='contained'
      >
        {t(general_messages.save)}
      </ColoredButton>
    </ButtonsGrid>
  );

  return (
    <DialogGrid
      color='secondary'
      data-testid='dsar-create-case-dialog'
      dialogActions={dialogActions}
      displayTitle
      fullWidth
      loading={false}
      loadingCover={false}
      maxWidth='md'
      noPadding={false}
      onClose={handleClose}
      open={open}
      scrollDisabled={false}
      sending={false}
      title={title}
      withCloseConfirmation={false}
    >
      <Typography className={styles.dialogIntro} color='textSecondary'>
        {intro}
      </Typography>
      {domainValuesLoading ? (
        <Typography>{t(dsar_messages.cases.create_dialog.domain_values_loading)}</Typography>
      ) : (
        <form id={FORM_ID} onSubmit={formik.handleSubmit}>
          <Typography className={styles.formSection} variant='h6'>
            {t(dsar_messages.cases.create_dialog.requester_section)}
          </Typography>
          <div className={styles.formGrid}>
            <FormTextInput
              formik={formik}
              fullWidth
              id='requesterName'
              label={t(dsar_messages.cases.create_dialog.requester_name)}
              required
            />
            <FormTextInput
              formik={formik}
              fullWidth
              id='requesterEmail'
              label={t(dsar_messages.cases.create_dialog.requester_email)}
              type='email'
            />
            <FormTextInput formik={formik} fullWidth id='requesterPhone' label={t(dsar_messages.cases.create_dialog.requester_phone)} />
            <FormTextInput
              formik={formik}
              fullWidth
              id='requesterCustomerNumber'
              label={t(dsar_messages.cases.create_dialog.requester_customernumber)}
            />
            <FormTextInput formik={formik} fullWidth id='requesterSsn' label={t(dsar_messages.cases.create_dialog.requester_ssn)} />
            <FormTextInput
              className={styles.formFullWidth}
              formik={formik}
              fullWidth
              id='requesterAddressLine1'
              label={t(dsar_messages.cases.create_dialog.requester_address_line1)}
            />
            <FormTextInput
              className={styles.formFullWidth}
              formik={formik}
              fullWidth
              id='requesterAddressLine2'
              label={t(dsar_messages.cases.create_dialog.requester_address_line2)}
            />
            <FormTextInput
              formik={formik}
              fullWidth
              id='requesterPostalCode'
              label={t(dsar_messages.cases.create_dialog.requester_postalcode)}
            />
            <FormTextInput formik={formik} fullWidth id='requesterCity' label={t(dsar_messages.cases.create_dialog.requester_city)} />
            <FormTextInput formik={formik} fullWidth id='requesterCountry' label={t(dsar_messages.cases.create_dialog.requester_country)} />
          </div>
          <Typography className={styles.formSection} variant='h6'>
            {t(dsar_messages.cases.create_dialog.case_section)}
          </Typography>
          <div className={styles.formGrid}>
            <FormSelect
              formik={formik}
              fullWidth
              id='statusId'
              label={t(dsar_messages.cases.create_dialog.status)}
              options={toOptions(caseStatusValues)}
              required
            />
            <FormDateInput
              dateTime={false}
              formik={dateFormik}
              fullWidth
              id='receivedAt'
              inputProps={{ 'aria-label': t(dsar_messages.cases.create_dialog.received_at), 'aria-required': true }}
              invalidDateMessage={t(validation_messages.invalid_date)}
              label={t(dsar_messages.cases.create_dialog.received_at)}
              maxDate={isEditMode ? undefined : toPickerDate(dateLimits.today)}
              maxDateMessage={t(validation_messages.date_cannot_be_in_future)}
              required
            />
            <FormDateInput
              dateTime={false}
              formik={dateFormik}
              fullWidth
              id='dueDate'
              inputProps={{ 'aria-label': t(dsar_messages.cases.create_dialog.due_date), 'aria-required': true }}
              invalidDateMessage={t(validation_messages.invalid_date)}
              label={t(dsar_messages.cases.create_dialog.due_date)}
              maxDate={isEditMode ? undefined : toPickerDate(dateLimits.maximumDueDate)}
              maxDateMessage={t(validation_messages.dsar_due_date_maximum)}
              minDate={isEditMode ? undefined : toPickerDate(dateLimits.today)}
              minDateMessage={t(validation_messages.date_cannot_be_in_past)}
              required
            />
            <div className={styles.formFullWidth}>
              <div className={styles.groupedFields}>
                <FormSelect
                  className={styles.formFullWidth}
                  disabled={identityLocked}
                  formik={formik}
                  fullWidth
                  id='identityVerificationStatusId'
                  label={t(dsar_messages.cases.create_dialog.identity_verification_status)}
                  options={toOptions(identityStatusValues)}
                  required
                />
                <FormTextInput
                  className={styles.formFullWidth}
                  disabled={identityLocked}
                  formik={formik}
                  fullWidth
                  id='identityVerificationNotes'
                  inputProps={{ className: styles.resizableTextarea }}
                  label={t(dsar_messages.cases.create_dialog.identity_verification_notes)}
                  multiline
                />
              </div>
            </div>
            <div className={styles.formFullWidth}>
              <FormControl
                className={styles.formFullWidth}
                error={formik.touched.requestTypeIds && Boolean(formik.errors.requestTypeIds)}
                fullWidth
              >
                <InputLabel className={styles.checkboxGroupLabel} shrink>
                  {t(dsar_messages.cases.create_dialog.request_types)} *
                </InputLabel>
                <FormGroup className={styles.checkboxGroup}>
                  {requestTypeValues.map(requestType => (
                    <FormControlLabel
                      key={requestType.id}
                      control={
                        <Checkbox
                          checked={formik.values.requestTypeIds.includes(requestType.id)}
                          color='primary'
                          data-testid={`requestTypeIds-${requestType.id}-checkbox`}
                          disabled={requestTypesLocked}
                          name={String(requestType.id)}
                          onChange={handleRequestTypeChange}
                        />
                      }
                      label={safeT(t, requestType.nameTranslationKey || undefined, requestType.name)}
                    />
                  ))}
                </FormGroup>
                <FormHelperText className={styles.checkboxError}>
                  {formik.touched.requestTypeIds ? (formik.errors.requestTypeIds as string) : ''}
                </FormHelperText>
              </FormControl>
            </div>
            <FormTextInput
              className={styles.formFullWidth}
              formik={formik}
              fullWidth
              id='adminInstructionComment'
              inputProps={{ className: styles.resizableTextarea }}
              label={t(dsar_messages.cases.create_dialog.admin_instruction_comment)}
              multiline
            />
            <FormTextInput
              className={styles.formFullWidth}
              formik={formik}
              fullWidth
              id='instructionLink'
              label={t(dsar_messages.cases.create_dialog.instruction_link)}
            />
            {showPortabilityFields && (
              <>
                <FormTextInput
                  className={styles.formFullWidth}
                  formik={formik}
                  fullWidth
                  id='portabilityInstruction'
                  inputProps={{ className: styles.resizableTextarea }}
                  label={t(dsar_messages.cases.create_dialog.portability_instruction)}
                  multiline
                />
                <FormTextInput
                  className={styles.formFullWidth}
                  formik={formik}
                  fullWidth
                  id='portabilityOutputLink'
                  label={t(dsar_messages.cases.create_dialog.portability_output_link)}
                />
              </>
            )}
          </div>
        </form>
      )}
    </DialogGrid>
  );
};

DsarCaseCreateDialog.defaultProps = {
  existingCase: null,
};

export default DsarCaseCreateDialog;
